<template>
  <div style="margin:20px">
    <FiPageRail title="Event Notes History" />

    <!-- date controls mirror the Alert History / Delivery Failures header: quick
         ranges, then the From/To pickers and their shift arrows behind Custom.
         the chips and arrows reload on their own; a hand-picked From/To waits
         for Get Results so setting two dates fires one query, not two. -->
    <div class="en-toolbar">
      <div class="en-range-group" role="group" aria-label="Event notes date range">
        <button
          v-for="option in dateRangeOptions"
          :key="option"
          type="button"
          class="en-range-btn"
          :class="{ 'en-range-btn--active': isActiveRange(option) }"
          :aria-pressed="isActiveRange(option) ? 'true' : 'false'"
          :disabled="loading"
          @click="set_days_range(option)"
        >{{ option }}d</button>
        <!-- collapsed at every width: the two pickers plus four arrows are the
             widest thing on this row and most visits never leave the chips. it
             reads active (and opens on its own) whenever the loaded window is
             not one of the chips -- a bespoke range must never look like no
             selection at all. -->
        <button
          type="button"
          class="en-range-btn en-range-btn--custom"
          :class="{ 'en-range-btn--active': datesOpen }"
          :aria-pressed="datesOpen ? 'true' : 'false'"
          :aria-expanded="datesOpen ? 'true' : 'false'"
          aria-controls="en-toolbar-dates"
          title="Set a specific From / To window"
          @click="show_custom_dates = !show_custom_dates"
        >Custom</button>
      </div>

      <!-- back arrows flank the from date, forward arrows the to date; the
           double arrows shift by the width of the loaded window, the singles
           by a day -->
      <div
        id="en-toolbar-dates"
        class="en-toolbar-dates"
        :class="{ 'en-toolbar-dates--open': datesOpen }"
      >
        <b-button-group class="toolbar-date-shifts">
          <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" :disabled="loading" title="Shift back one window" v-on:click.prevent="shift_detail_graph_date(-selectedDayCount)"><i class="fa fa-angle-double-left" aria-hidden="true"></i></b-button>
          <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" :disabled="loading" title="Previous day" v-on:click.prevent="shift_detail_graph_date(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
        </b-button-group>
        <span class="en-field">
          <span class="en-label">From</span>
          <b-form-input v-model="fromDateIso" type="date" size="sm" class="en-date-input" :min="dateMinIso" :max="dateMaxIso"/>
        </span>
        <span class="en-field">
          <span class="en-label">To</span>
          <b-form-input v-model="toDateIso" type="date" size="sm" class="en-date-input" :min="dateMinIso" :max="dateMaxIso"/>
        </span>
        <b-button-group class="toolbar-date-shifts">
          <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" :disabled="loading" title="Next day" v-on:click.prevent="shift_detail_graph_date(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
          <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" :disabled="loading" title="Shift forward one window" v-on:click.prevent="shift_detail_graph_date(selectedDayCount)"><i class="fa fa-angle-double-right" aria-hidden="true"></i></b-button>
        </b-button-group>
      </div>

      <b-button size="sm" v-on:click.prevent="load_data()" :disabled="loading">Get Results</b-button>
      <span class="en-field">
        <span class="en-label">Types</span>
        <b-form-select size="sm" class="en-select" v-model="event_type" :options="type_options"/>
      </span>
      <span class="en-field">
        <span class="en-label">Filter Results</span>
        <b-form-input size="sm" class="en-filter-input" v-model="event_filter" placeholder="bookingID, vehicle, driver, notes"/>
      </span>
      <b-button size="sm" v-if="summary.event_categories_options" v-on:click.prevent="send_category_reports()" :disabled="loading">Send Payroll Category Reports</b-button>
    </div>

    <!-- the query stops at the server's cap newest-first, so a capped range is
         missing its OLDEST notes. say so rather than showing a short list that
         looks complete. -->
    <b-alert v-if="!loading && summary.truncated" show variant="warning" class="en-truncation-warning">
      <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
      {{ truncationMessage }}
    </b-alert>

    <!-- results meta line, in the Alert History voice (.al-results-meta): the
         counts are context for the table, not the headline, so they read as a
         quiet strip above it rather than three bordered 22px boxes. the ERM
         log has no stats row of its own -- it hangs its counts off the filter
         chips -- so this is the closest thing in the same family.
         the loaded/total pair says the same thing the old Total + Filtered
         boxes did, in the phrasing Alert History already uses. -->
    <div v-if="!loading" class="en-results-meta">
      <span class="en-results-count">{{ items.length }} of {{ summary.total_cnt }} notes</span>
      <span v-if="summary.event_categories_options" class="en-field en-meta-category">
        <span class="en-label">Category</span>
        <b-form-select size="sm" class="en-select en-select--narrow" v-model="category_filter" :options="summary.event_categories_options"/>
      </span>
<!--      <b-button size="sm" variant="success" @click="download">Download</b-button>-->
    </div>

    <!-- mobile card list (<=768px), the ERM log's .od-mobile-list. the dense
         nine-column table is unreadable on a phone, so each note becomes a
         full-width card: the note itself is the headline and the facets drop
         to a meta line under it. class names reused from ops_desk.vue for the
         same reason as the table below -- midnight.css themes them unscoped.
         cards follow the loaded order (newest first, the order the api
         returns); the table's own column sorting is desktop-only. -->
    <div v-if="isMobileView" class="od-mobile-list">
      <div v-if="loading" class="text-center text-danger my-2">
        <b-spinner class="align-middle"></b-spinner>
      </div>
      <template v-else>
        <!-- a div, not a button: unlike an ERM event these rows open nothing,
             and a tap target that does nothing is worse than none. --static
             drops the hover fill and the pointer cursor that go with it. -->
        <div
          v-for="item in pagedItems"
          :key="'en-mbl-' + item.event_hash"
          class="od-mobile-card od-mobile-card--static"
        >
          <div class="od-mobile-card-head">
            <span class="od-mobile-card-id">{{ item.bookingID || item.event_type || '-' }}</span>
            <span class="od-mobile-card-chips">
              <span v-for="cat in categoryList(item.categories)" :key="cat" class="od-cat-chip">{{ cat }}</span>
            </span>
          </div>
          <div class="od-mobile-card-subject">{{ item.notes || '(no note)' }}</div>
          <div class="od-mobile-card-meta">
            <span v-if="item.event_date"><i class="fa fa-calendar od-mobile-meta-icon" aria-hidden="true"></i>{{ item.event_date }}</span>
            <span v-if="item.vehicle"><i class="fa fa-bus od-mobile-meta-icon" aria-hidden="true"></i>{{ item.vehicle }}</span>
            <span v-if="item.driver"><i class="fa fa-user od-mobile-meta-icon" aria-hidden="true"></i>{{ item.driver }}</span>
            <span v-if="item.updated_by"><i class="fa fa-user-circle-o od-mobile-meta-icon" aria-hidden="true"></i>{{ item.updated_by }}</span>
            <span class="od-mobile-card-dt">{{ item.processed_time | formatDateTimeSmall }}</span>
          </div>
        </div>
        <div v-if="!items.length" class="od-mobile-empty">No notes match these filters.</div>
      </template>
    </div>

    <!-- od-table-inline is the ERM Event Log's table shell. the class name is
         load-bearing, not decorative: midnight.css themes `.od-table-inline`
         unscoped, so reusing it gets the dark palette for free, while the
         light rules (scoped to each component by `>>>`) are restated below. -->
    <div v-show="!isMobileView" class="od-table-inline">
      <b-table :busy="loading" sticky-header="70vh" :items="items" :fields="fields"
               :current-page="currentPage" :per-page="perPage" small show-empty>

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <!-- bootstrap-vue 2.0.0-rc.28 uses bare-key cell slots (slot="notes"),
             not the later slot="cell(notes)" form -- the cell() form is
             silently ignored here, rendering nothing. same note as
             ops_desk.vue and support_ticket_list.vue. -->
        <template slot="event_date" slot-scope="row">
          <span class="od-list-dt">{{ row.item.event_date }}</span>
        </template>

        <template slot="categories" slot-scope="row">
          <template v-if="categoryList(row.item.categories).length">
            <span v-for="cat in categoryList(row.item.categories)" :key="cat" class="od-cat-chip" :title="cat">{{ cat }}</span>
          </template>
          <span v-else class="od-cell-none">-</span>
        </template>

        <!-- the note is shown in full: it wraps to as many lines as it needs
             and the row grows with it, the way the ERM log shows a whole
             description. no clamp -- this column is what the page is for. -->
        <template slot="notes" slot-scope="row">
          <span v-if="row.item.notes" class="od-snippet">{{ row.item.notes }}</span>
          <span v-else class="od-cell-none">-</span>
        </template>

        <template slot="empty">
          <div class="od-table-empty">No notes match these filters.</div>
        </template>

        <template slot="message" slot-scope="row">
          <div style="text-align: left" v-for="msg in row.item.message.split(',')">
            <span v-html="msg"/>
          </div>
        </template>

        <template slot="booking_id" slot-scope="row">
          <router-link v-if="user.types[user.tenant] == 'CM'" style="font-weight: bold;" :to="{ path: '/actuals?booking_id=' + row.item.booking_hash }">{{ row.item.booking_id }}</router-link>
          <router-link v-else style="font-weight: bold;" :to="{ path: '/actuals_tbn?booking_id=' + row.item.booking_hash }">{{ row.item.booking_id }}</router-link>
        </template>

        <template slot="processed_time" slot-scope="row">
          <span class="od-list-dt" :title="row.item.processed_time">{{row.item.processed_time | formatDateTimeSmall}}</span>
        </template>

        <template slot="start_datetime" slot-scope="row">
          {{row.item.start_datetime | formatDateTimeSmall}}
        </template>

        <template slot="message" slot-scope="row">
          <div style="text-align: left; font-family: monospace; white-space: normal !important;" v-html="row.item.message.replaceAll('\n','<br/>')"/>
        </template>

        <template slot="email" slot-scope="row">
          <template v-if="row.item.email">
            <div style="text-align: left;"  v-for="val in row.item.email.split(',')">
              {{val}}
            </div>
          </template>
        </template>

        <template slot="finish_datetime" slot-scope="row">
          {{row.item.finish_datetime | formatDateTimeSmall}}
        </template>

        <template slot="details" slot-scope="row">
          <b-button size="sm" @click="row.toggleDetails">
            {{ row.detailsShowing ? '-' : '+' }}
          </b-button>
        </template>
      </b-table>
    </div>

    <!-- one pagination control for both views: it used to live inside the
         table wrapper, which the mobile branch now hides -->
    <b-row>
      <b-col>
        <b-pagination
            v-model="currentPage"
            :total-rows="items.length"
            :per-page="perPage"
            align="center"
        >
        </b-pagination>
      </b-col>
    </b-row>
  </div>
</template>

<script>

import {api_url, get_event_notes, send_event_notes_payroll_category_reports} from '../networking/apis'
import {mapState} from "vuex";
import moment from 'moment'
import {justDate} from "@/helpers/dates";
import axios from 'axios';

const DATE_RANGE_OPTIONS = [1, 7, 30, 90]

export default {
  name: 'event_notes_list',
  components: {
  },

  data() {
    return {
      event_filter: '',
      category_filter: '',
      items: [],
      items_full: [],
      summary: [],
      loading: true,
      details_fromDate: justDate(moment().add(-1, 'days')),
      details_toDate: justDate(moment()),
      event_type: '',
      confirmed_state:'',
      // disclosure for the From/To pickers and their shift arrows (see the
      // Custom chip) -- collapsed at every width, not just on phones
      show_custom_dates: false,
      // <=768px swaps the dense nine-column b-table for a tap-friendly card
      // list, matching ops_desk / support_ticket_list. kept in sync by a
      // resize listener wired in mounted().
      isMobileView: false,
      totalRows: 1,
      currentPage: 1,
      perPage: 100,
      pages: [],
      type_options: [
        { value: '', text: 'All Types' },
        { value: 'payrollreport', text: 'Payroll Report' },
        { value: 'billingreport', text: 'Billing Report' },
      ],
      fields_all: [],
      // column widths follow the ERM log's shape: the facet columns shrink to
      // their content and the free-text column absorbs whatever is left, so
      // the note is the part of the row that gets the width. the old fixed
      // 400px on categories/notes did the opposite -- it pinned the two
      // widest columns and let the rest sprawl.
      fields: [
        { key: 'event_date',sortable: true, thStyle: { width: "104px" }},
        { key: 'event_type',sortable: true, thStyle: { width: "120px" }},
        //{ key: 'event_target',sortable: true},
        { key: 'bookingID',sortable: true, thStyle: { width: "96px" }},
        { key: 'vehicle',sortable: true, thStyle: { width: "84px" }},
        { key: 'driver',sortable: true, thClass: 'od-driver-cell', tdClass: 'od-driver-cell'},
        { key: 'processed_time', sortable: true, thStyle: { width: "112px" }},
        { key: 'updated_by', sortable: true, thClass: 'od-assigned-cell', tdClass: 'od-assigned-cell'},
        { key: 'categories', label: 'Payroll Categories', sortable: true, thClass: 'en-cat-cell', tdClass: 'en-cat-cell'},
        { key: 'notes', label: 'Notes', sortable: true, tdClass: 'od-snippet-cell'},
      ]
      ,
    }
  },

  async created() {
    this.fields_all = this.fields
    this.load_data()
  },

  mounted() {
    // card-vs-table switch. window.innerWidth is the proven-reliable
    // breakpoint source on ios (see ops_desk / support_ticket_list).
    this.isMobileView = window.innerWidth <= 768
    this._onListResize = () => { this.isMobileView = window.innerWidth <= 768 }
    window.addEventListener('resize', this._onListResize)
  },

  beforeDestroy() {
    if (this._onListResize) window.removeEventListener('resize', this._onListResize)
  },
  methods: {
    async load_data() {
      this.loading = true
      try {
        const {value: { data = [] } } = await get_event_notes(this.user.tenant, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'))
        this.summary = data.summary
        moment.tz.setDefault(this.summary.timezone)
        this.items = data.results
        this.items_full = data.results
        this.filter_results()
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.loading = false
    },
    async send_category_reports() {
      try {
        const {value: { data = [] } } = await send_event_notes_payroll_category_reports(this.user.tenant, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'))
        this.$emit('showAlert', 'sending event category reports', 'success', 5000)
      } catch(e) {
        this.$emit('showAlert', 'error sending reports, try logging in again or contact support if needed', 'danger', 5000)
      }
    },
    async download(){
      this.$emit('showAlert', 'processing download request', 'info', 5000)
      axios.defaults.headers.common.Accept = {}
      axios.post(
          `${api_url}bookings/events/download`,
          {
            tenant: this.user.tenant,
            start_date: moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            end_date: moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            event_type: this.event_type,
            event_filter: this.event_filter,
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt')}`,
              Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
      ).then((response) => {
        axios.defaults.headers.common.Accept = 'application/json'
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
            'download',
            `event-notes-${this.user.tenant}-${this.details_fromDate}.csv`
        )
        document.body.appendChild(link)
        link.click()
        this.$emit('showAlert', 'download complete', 'success', 5000)
      }).catch(e => {
        axios.defaults.headers.common.Accept = 'application/json'
        this.$emit('showAlert', 'alert data could not be downloaded.', 'danger')
      })
    },
    async shift_detail_graph_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
      await this.load_data()
    },
    set_days_range(option) {
      this.details_fromDate = justDate(moment().subtract(option, 'days'))
      this.details_toDate = justDate(moment())
      // a preset window is the answer to "custom", so the disclosure closes with
      // it -- otherwise the pickers stay open showing dates the chip beside them
      // already describes
      this.show_custom_dates = false
      this.load_data()
    },
    // active state is derived from the current from/to dates rather than a
    // separately tracked "selected option", so it stays correct whether the
    // range came from a quick button, the date pickers, or the shift arrows
    isActiveRange(option) {
      return this.details_fromDate === justDate(moment().subtract(option, 'days')) &&
        this.details_toDate === justDate(moment())
    },
    filter_results() {
      this.items = this.items_full
      if (this.category_filter)
        this.items = this.items.filter(item => item.categories && item.categories.includes(this.category_filter))
      if (this.event_type != '')
        this.items = this.items.filter(item => (item.event_type == this.event_type))
      if (this.event_filter != '') {
        const val = this.event_filter
        this.items = this.items.filter(item => (this.contains(item.notes, val) || this.contains(item.event_target, val) || this.contains(item.updated_by, val)))
      }
    },
    // the api joins the tenant's payroll categories into one ", "-separated
    // string; the log renders each as its own chip, so split it back apart
    categoryList(categories) {
      if (!categories) return []
      return categories.split(',').map(c => c.trim()).filter(c => c)
    },
    contains(val1, val2) {
      if (val1 != null) {
        return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
      }
      return false;
    }

  },
  computed:{
    ...mapState({
      user: state => state.profile.user
    }),
    dateRangeOptions() {
      return DATE_RANGE_OPTIONS
    },
    // the native date inputs speak ISO; the rest of the page (and the api
    // calls) keep the legacy MM-DD-YYYY strings, so these adapt at the edge.
    // an empty value (the input's own clear affordance) is ignored rather than
    // propagated -- this page always queries a bounded window.
    fromDateIso: {
      get() { return moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD') },
      set(v) { if (v) this.details_fromDate = moment(v, 'YYYY-MM-DD').format('MM-DD-YYYY') },
    },
    toDateIso: {
      get() { return moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD') },
      set(v) { if (v) this.details_toDate = moment(v, 'YYYY-MM-DD').format('MM-DD-YYYY') },
    },
    // event notes carry a 360-day TTL (event_notes_manager.save_event_notes
    // writes expiry_time at now + 360d), so anything older than this has
    // already been reaped -- picking further back can only return nothing.
    // the native input enforces these as its own min/max.
    dateMinIso() {
      return moment().subtract(360, 'days').format('YYYY-MM-DD')
    },
    dateMaxIso() {
      return moment().add(90, 'days').format('YYYY-MM-DD')
    },
    // b-table slices `items` by current-page/per-page itself; the card list
    // has to do its own, so both views answer the same pagination control
    pagedItems() {
      const start = (this.currentPage - 1) * this.perPage
      return this.items.slice(start, start + this.perPage)
    },
    // the window matches none of the range chips -- set by the pickers or by
    // the shift arrows
    isCustomRange() {
      return !DATE_RANGE_OPTIONS.some(option => this.isActiveRange(option))
    },
    // whether the From/To cluster shows: either the user asked for it, or the
    // window is already bespoke and hiding the dates that produced it would
    // leave the row claiming no selection at all
    datesOpen() {
      return this.show_custom_dates || this.isCustomRange
    },
    // length of the current from/to window in days, so the double-arrow shift
    // moves by "one more of these" instead of a fixed step
    selectedDayCount() {
      const diff = moment(this.details_toDate, 'MM-DD-YYYY').diff(moment(this.details_fromDate, 'MM-DD-YYYY'), 'days')
      return diff > 0 ? diff : 1
    },
    // the server sends the cap it applied, so the warning quotes the real
    // number rather than a copy here that can drift out of step with the api.
    // it degrades to the unnumbered sentence if an older api omits the field.
    truncationMessage() {
      const limit = this.summary && this.summary.results_limit
      const shown = limit ? `the ${Number(limit).toLocaleString()} most recent notes` : 'only the most recent notes'
      return `This range has more notes than one request returns. Showing ${shown} \u2014 older notes in the range are not listed. Pick a shorter range to see them.`
    },
  },
  watch: {
    user: {
      deep: true,
    },
    event_type() {
      this.filter_results();
    },
    category_filter() {
      this.filter_results();
    },
    event_filter() {
      this.filter_results()
    }
  }
}
</script>

<!-- date-control recipe shared with the Alerts Dashboard / Alert History /
     Delivery Failures headers: same sizes, same tokens, so the three pages
     read as one control. scoped, so it cannot leak the way the block below
     does. -->
<style scoped>
.en-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 8px;
  margin: 24px 20px;
  font-size: 13px;
}

.en-label {
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--fi-text-muted);
  margin-right: 2px;
  white-space: nowrap;
}

.en-field {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.en-toolbar .en-select { width: 240px; font-size: 13px; }
.en-toolbar .en-filter-input { width: 230px; font-size: 13px; }

.en-range-group {
  display: inline-flex;
  border: 1px solid var(--fi-line-strong);
  border-radius: var(--fi-radius-sm);
  overflow: hidden;
}

.en-range-btn {
  background: var(--fi-surface);
  color: var(--fi-text-muted);
  border: none;
  border-left: 1px solid var(--fi-line-strong);
  padding: 5px 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}

.en-range-btn:first-child { border-left: none; }

.en-range-btn:hover:not(:disabled) {
  background: var(--fi-surface-sunken);
  color: var(--fi-text);
}

.en-range-btn:disabled { cursor: default; opacity: 0.6; }

.en-range-btn--active,
.en-range-btn--active:hover:not(:disabled) {
  background: var(--fi-brand);
  color: var(--fi-brand-contrast);
}

/* collapsed until the Custom chip opens it, or until the loaded window stops
   matching one of the chips */
.en-toolbar-dates { display: none; }

.en-toolbar-dates--open {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
}

.en-toolbar .en-date-input {
  font-size: 13px;
  width: 150px;
}

.toolbar-date-shift-btn {
  padding: 0.15rem 0.35rem;
  line-height: 1;
  min-width: 1.65rem;
}

.toolbar-date-shift-btn .fa {
  font-size: 13px;
  font-weight: bold;
  vertical-align: middle;
}

/* the date cluster takes its own full-width line once the toolbar is too
   narrow to hold it beside the chips */
@media (max-width: 900px) {
  .en-toolbar-dates--open {
    flex: 1 1 100%;
    justify-content: center;
  }
}

.en-truncation-warning {
  margin: 0 20px 12px;
  font-size: 13px;
}

/* results meta line above the table: same 12.5px muted voice as Alert
   History's .al-results-meta, with the count itself in full ink so the one
   number people scan for still reads first */
.en-results-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  margin: 0 20px 10px;
  font-size: 12.5px;
  color: var(--fi-text-muted);
}

.en-results-count {
  font-weight: 600;
  color: var(--fi-text);
}

/* the category select closes the line on the right, the way the base select
   does on Alert History */
.en-meta-category { margin-left: auto; }
.en-results-meta .en-select--narrow { width: 170px; font-size: 13px; }

/* ---- results table: the ERM Event Log's list styling (ops_desk.vue's
   .od-table-inline). the light rules are restated here rather than shared
   because ops_desk scopes them to its own component with `>>>`; the class
   NAMES are kept identical so midnight.css -- which targets .od-table-inline,
   .od-snippet, .od-list-dt and .od-cat-chip unscoped -- themes this table
   too, with no midnight rules of its own. ---- */
.od-table-inline >>> .table.b-table { font-size: 13px; border-collapse: collapse; }
.od-table-inline >>> .table.b-table th,
.od-table-inline >>> .table.b-table td {
  border-left: none !important; border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
/* a full note can run several lines, so body cells align to the top: the date,
   chips and stamp sit level with the first line of the note instead of
   floating halfway down a tall row */
.od-table-inline >>> .table.b-table tbody td { vertical-align: top !important; }
.od-table-inline >>> .table.b-table th:first-child,
.od-table-inline >>> .table.b-table td:first-child { padding-left: 12px !important; }
.od-table-inline >>> .table.b-table th:last-child,
.od-table-inline >>> .table.b-table td:last-child   { padding-right: 12px !important; }
.od-table-inline >>> .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; color: #555;
  position: sticky; top: 0; z-index: 2;
  box-shadow: inset 0 -2px 0 0 #ccc;
}
/* light zebra striping, painted on the td rather than the tr so it stacks the
   same way the hover fill does; the hover rule has equal specificity and must
   stay AFTER this one to win on a striped row. these also have to out-specify
   the bare `tr:nth-child(even)` and `.table td` rules that this file's global
   block below -- and a dozen other report components -- leak page-wide. */
.od-table-inline >>> .table.b-table tbody tr:nth-child(even) td { background-color: #f7f8fa; }
/* no cursor:pointer, unlike the ERM log: these rows do not open anything */
.od-table-inline >>> .table.b-table tbody tr:hover td { background-color: #f0f5ff; }

.od-table-inline >>> th[aria-sort] { background-image: none !important; padding-right: 0.75rem !important; white-space: nowrap; }
.od-table-inline >>> th[aria-sort]::before { display: none !important; }
.od-table-inline >>> th[aria-sort]::after  { display: inline-block !important; margin-left: 4px; font-size: 10px; color: #999; vertical-align: middle; float: none !important; width: auto !important; height: auto !important; opacity: 1 !important; }
.od-table-inline >>> th[aria-sort="none"]::after       { content: "\2195" !important; }
.od-table-inline >>> th[aria-sort="ascending"]::after  { content: "\25B2" !important; color: var(--fi-rail-accent); }
.od-table-inline >>> th[aria-sort="descending"]::after { content: "\25BC" !important; color: var(--fi-rail-accent); }

/* the note column is the flexible one: width:100% makes it absorb every pixel
   the shrink-to-fit and fixed-width columns don't take, and the note wraps
   inside it */
.od-table-inline >>> td.od-snippet-cell { width: 100%; }
/* shrink-to-fit + a cap: width:1% + nowrap sizes the column to its content,
   and the max-width stops one outlier value taking the note column's width
   away from every row */
.od-table-inline >>> .od-driver-cell {
  white-space: nowrap;
  width: 1%;
  max-width: 132px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.od-table-inline >>> .od-assigned-cell {
  white-space: nowrap;
  width: 1%;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* categories wrap to a second line of chips rather than ellipsizing: a note
   can carry several and losing one silently would misread the payroll row */
.od-table-inline >>> .en-cat-cell {
  width: 1%;
  min-width: 130px;
  max-width: 210px;
}
.od-cat-chip {
  display: inline-block;
  padding: 2px 8px;
  margin: 1px 0;
  font-size: 11.5px;
  line-height: 1.4;
  font-weight: 600;
  border-radius: 4px;
  background: #eef4fb;
  color: #1f5582;
  border: 1px solid #d6e1ee;
  white-space: nowrap;
}
.od-cat-chip + .od-cat-chip { margin-left: 4px; }

.od-snippet {
  display: block;
  color: #374151;
  white-space: normal;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.od-list-dt {
  display: inline-block;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: #374151;
}

.od-cell-none { color: #9aa4b1; }

.od-table-empty {
  padding: 18px 12px;
  text-align: center;
  color: var(--fi-text-muted);
  font-size: 13px;
}

/* ---- mobile card list (<=768px, rendered via isMobileView): the ERM log's
   .od-mobile-list. same class names, same reason as the table -- midnight.css
   themes .od-mobile-card and friends unscoped, so the dark palette comes for
   free and the light rules are restated here. ---- */
.od-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* extend the bottom past the iphone home indicator so the last card isn't
     tucked under it; env() is 0 on non-notched devices, leaving the base 16px */
  padding: 6px 12px 4px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
}
.od-mobile-card {
  display: block;
  width: 100%;
  text-align: left;
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-line, #e0e0e0);
  border-radius: 8px;
  padding: 10px 12px;
}
/* the ERM card is a tap target that opens an event; these rows open nothing,
   so no pointer and no hover fill promising a tap that does nothing */
.od-mobile-card--static { cursor: default; }
.od-mobile-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.od-mobile-card-id {
  font-size: 12px;
  font-weight: 700;
  color: var(--fi-text-muted, #6b7280);
  font-variant-numeric: tabular-nums;
}
.od-mobile-card-chips {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
/* the note is the headline here, and shown in full -- same rule as the
   table's .od-snippet: the card grows instead of clamping */
.od-mobile-card-subject {
  font-size: 14px;
  font-weight: 600;
  color: var(--fi-text, #333);
  line-height: 1.35;
  margin-bottom: 6px;
  word-break: break-word;
}
.od-mobile-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  font-size: 12px;
  color: var(--fi-text-muted, #555);
}
.od-mobile-meta-icon { margin-right: 4px; color: #9ca3af; }
.od-mobile-card-dt {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.od-mobile-empty {
  color: var(--fi-text-muted, #888);
  font-size: 13px;
  text-align: center;
  padding: 20px 10px;
}

/* the filter toolbar is the widest thing on the page; on a phone the selects
   and the note filter take a full line each rather than being squeezed */
@media (max-width: 768px) {
  .en-toolbar { margin: 16px 12px; }
  .en-toolbar .en-select,
  .en-toolbar .en-filter-input { width: 100%; }
  /* the type select and the note filter each take a line; the two date
     fields are the exception -- they stay side by side so the shift arrows
     keep flanking them */
  .en-toolbar > .en-field { flex: 1 1 100%; }
  .en-toolbar-dates--open { flex-wrap: wrap; justify-content: center; }
  .en-toolbar-dates .en-field { flex: 0 1 auto; }
  .en-toolbar .en-date-input { width: 140px; }
  .en-results-meta { margin: 0 12px 8px; }
  .en-meta-category { margin-left: 0; flex: 1 1 100%; }
  .en-results-meta .en-select--narrow { width: 100%; }
}
</style>

<style>

div.row:nth-child(even) {
  background-color: #f2f2f2 !important;
}

tr:nth-child(even) {
  background-color: #f2f2f2;
}

.header {
  font-weight: bold;
}

div.col {
  border: 1px solid grey;
}

.table td {
  padding: .1rem !important;
}

.mvmt-table td {
  vertical-align: middle !important;
}

div.wrap {
  border: 1px solid grey;
  flex-wrap: wrap;
  display: flex;
  inline-size: 150px;
  /* overflow-wrap: break-word; */
}

</style>

